import { CARD_API_ENDPOINT } from '@/config/consts'
import { EVC_ADDRESS } from '@/config/euler-contracts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { CREDIT_TYPES, EULER_CARD_SUB_ACCOUNT_ID } from '@/features/card/constants'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { type Address } from 'viem'
import { useAccount, useChainId, usePublicClient, useReadContract, useSendTransaction } from 'wagmi'
import { createSmartFetch } from './useAuthenticatedMutation'
import { useCreditReceiver } from './useCreditReceiver'

interface UseEulerDelegationParams {
  enabled?: boolean
  subAccountId?: number
  borrowVaultAddress?: Address
}

interface ResolveEulerDelegationSetupStateParams {
  controllerRequired: boolean
  isAuthorizationLoaded: boolean
  isControllerLoaded: boolean
  isLoadingResolvedOperator: boolean
  isOperatorAuthorized: boolean
  isOperatorError: boolean
  isControllerEnabled: boolean
}

export function resolveEulerDelegationSetupState({
  controllerRequired,
  isAuthorizationLoaded,
  isControllerLoaded,
  isLoadingResolvedOperator,
  isOperatorAuthorized,
  isOperatorError,
  isControllerEnabled,
}: ResolveEulerDelegationSetupStateParams): {
  hasLoadedSetupState: boolean
  isSetupComplete: boolean
  needsDelegation: boolean
} {
  const hasControllerEnabled = !controllerRequired || isControllerEnabled
  const isSetupComplete = isOperatorAuthorized && hasControllerEnabled
  const hasLoadedSetupState =
    (isAuthorizationLoaded && !isLoadingResolvedOperator && (!controllerRequired || isControllerLoaded)) || isOperatorError

  return {
    hasLoadedSetupState,
    isSetupComplete,
    needsDelegation: hasLoadedSetupState && !isSetupComplete,
  }
}

export function useEulerDelegation({
  enabled: externalEnabled = true,
  subAccountId = EULER_CARD_SUB_ACCOUNT_ID,
  borrowVaultAddress,
}: UseEulerDelegationParams = {}) {
  const { address } = useAccount()
  const chainId = useChainId()
  const queryClient = useQueryClient()
  const publicClient = usePublicClient()

  const hasValidSubAccount = Number.isInteger(subAccountId)
  const subAccountAddress =
    address && hasValidSubAccount ? getSubAccountAddress(address, subAccountId as number) : undefined

  const { sendTransactionAsync, isPending: isApproving, error: approveError, data: txHash } = useSendTransaction()

  const {
    creditReceiver: operatorAddress,
    isLoading: isLoadingOperator,
    isError: isOperatorError,
  } = useCreditReceiver({
    enabled: externalEnabled,
  })
  const resolvedOperatorAddress = operatorAddress
  const isLoadingResolvedOperator = !resolvedOperatorAddress && isLoadingOperator

  // Check if operator is authorized for the SUB-ACCOUNT
  const {
    data: isOperatorAuthorized,
    isLoading: isLoadingAuthorization,
    isSuccess: isAuthorizationLoaded,
  } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'isAccountOperatorAuthorized',
    args: subAccountAddress && resolvedOperatorAddress ? [subAccountAddress, resolvedOperatorAddress] : undefined,
    query: {
      enabled: externalEnabled && !!subAccountAddress && !!resolvedOperatorAddress,
    },
  })

  const {
    data: isControllerEnabled,
    isLoading: isLoadingController,
    isSuccess: isControllerLoaded,
  } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'isControllerEnabled',
    args: subAccountAddress && borrowVaultAddress ? [subAccountAddress, borrowVaultAddress] : undefined,
    query: {
      enabled: externalEnabled && !!subAccountAddress && !!borrowVaultAddress,
    },
  })

  const controllerRequired = !!borrowVaultAddress
  const { hasLoadedSetupState, isSetupComplete, needsDelegation } = resolveEulerDelegationSetupState({
    controllerRequired,
    isAuthorizationLoaded,
    isControllerLoaded,
    isLoadingResolvedOperator,
    isOperatorAuthorized: !!isOperatorAuthorized,
    isOperatorError,
    isControllerEnabled: !!isControllerEnabled,
  })

  const approveDelegation = useMutation({
    mutationFn: async () => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!subAccountAddress) {
        throw new Error('Missing Euler sub-account configuration')
      }

      if (!borrowVaultAddress) {
        throw new Error('Borrow vault address not available')
      }

      if (!publicClient) {
        throw new Error('Public client not available')
      }

      try {
        const delegateFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
          method: 'POST',
          body: JSON.stringify({
            creditType: CREDIT_TYPES.EULER_BORROW,
            chainID: chainId,
            userParams: {
              borrowVaultAddress,
              subAccountId,
            },
          }),
        })
        const apiData = await delegateFetch({})
        if (!apiData?.data?.to || !apiData?.data?.callData) {
          throw new Error('Invalid delegation response from server')
        }
        const { to, callData, value } = apiData.data

        const hash = await sendTransactionAsync({
          to,
          value: BigInt(value || 0),
          data: callData,
        })

        await waitForTxReceipt(publicClient, hash)

        // Await the refetch so consumers that read `needsDelegation` see the
        // post-approval state as soon as `mutateAsync` resolves. Without the
        // await, there's a brief window where the button re-enables while the
        // on-chain read is still catching up, which can enqueue duplicate txs.
        await queryClient.invalidateQueries({
          queryKey: ['readContract'],
        })

        return hash
      } catch (error) {
        // Enhance error messages for user rejections
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (
          errorMessage.includes('rejected') ||
          errorMessage.includes('denied') ||
          errorMessage.includes('cancelled')
        ) {
          throw new Error('User rejected the position authorization request')
        }
        throw error
      }
    },
  })

  const resetError = () => {
    approveDelegation.reset()
  }

  const isMutationPending = approveDelegation.isPending
  const isConfirming = !isApproving && isMutationPending
  const isConfirmed = approveDelegation.isSuccess

  return {
    isOperatorAuthorized: !!isOperatorAuthorized,
    isControllerEnabled: !!isControllerEnabled,
    isSetupComplete,
    needsDelegation,
    isLoadingAuthorization:
      isLoadingAuthorization ||
      (controllerRequired && isLoadingController) ||
      (isLoadingResolvedOperator && !isOperatorError),
    isAuthorizationLoaded: hasLoadedSetupState,
    approveDelegation: approveDelegation.mutateAsync,
    isApproving,
    isConfirming,
    isConfirmed,
    txHash,
    approveError: approveError || approveDelegation.error,
    resetError,
    delegationTarget: resolvedOperatorAddress,
    subAccountAddress,
  }
}
